import { Injectable } from "@angular/core";
import { TOAST_TYPE } from "src/domain/extentions/toast";
import { ToastService } from "./toast.service";
import { TranslateService } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";
import { FALLBACK } from "src/fallbackMissingTranslationsHandler";
import { NonUserError, SilentError, asError } from "src/errors";

@Injectable({
  providedIn: "root",
})
export class ErrorReporterService {
  constructor(
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {
    this.handle = this.handle.bind(this);
    this.chassiNotFound = this.chassiNotFound.bind(this);
  }

  /**
   * Do default handling of errors.
   * Normal behavior is to pop up a user-error toast.
   * This behavior can be changed by using {@link SilentError} or
   * {@link NonUserError}
   */
  handle(thrown: unknown): void;
  handle(
    thrown: unknown,
    translateKey: string,
    interpolateParams?: Object,
  ): void;
  handle(
    thrown: unknown,
    translateKey?: string,
    interpolateParams?: Object,
  ): void {
    if (thrown instanceof SilentError) return;
    if (thrown instanceof NonUserError) {
      thrown = thrown.wrappedError ?? thrown;
    } else {
      if (translateKey == null) {
        translateKey = "errorMessages.default";
        interpolateParams = {
          [FALLBACK]: "Something went wrong. Reload the page and try again.",
        };
      }
      this.toastError(translateKey, interpolateParams);
    }
    console.error(asError(thrown));
  }

  /**
   * Notify the end user about the vehicle not being found.
   *
   * @param regNr The vehicle registration number that wasn't found
   * @param reason Optional underlaying reason (like thrown error) that
   * will be handled with the default error handler (wrapped in NonUserError)
   */
  chassiNotFound(regNr: string | null | undefined, reason?: unknown): void {
    this.toastError("errorMessages.chassiNotFound", {
      regNr,
    });
    if (reason != null) this.handle(new NonUserError(asError(reason)));
  }

  private toastError(
    translateKey: string,
    interpolateParams?: Object | undefined,
  ) {
    firstValueFrom(
      this.translateService.get(translateKey, interpolateParams),
    ).then(
      (val) => {
        this.toastService.createToast(val, TOAST_TYPE.DANGER);
      },
      (err) => {
        console.error("Error when fetching translation key", err);
        const fallback =
          this.translateService.parser.interpolate(
            interpolateParams &&
              FALLBACK in interpolateParams &&
              typeof interpolateParams[FALLBACK] === "string"
              ? interpolateParams[FALLBACK]
              : translateKey,
            interpolateParams,
          ) ?? translateKey;
        this.toastService.createToast(fallback, TOAST_TYPE.DANGER);
      },
    );
  }
}
